import "~/Core/Assets/css/style.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import React, { Suspense, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import Loader from "~/Core/Components/Extra/Loader";
import TypesafeI18n from "~/i18n/i18n-react";
import { loadLocaleAsync } from "~/i18n/i18n-util.async";
import AccountLayout from "~/Pages/Shared/Layout/Account";
import { useLanguageStore } from "~/Store/index";

const ViewError = React.lazy(() => import("./Pages/Shared/Error"));
const NotAllowed = React.lazy(() => import("./Pages/Shared/NotAllowed"));
const Portal = React.lazy(() => import("./Pages/Portal"));
const Dashboard = React.lazy(() => import("./Pages/Dashboard"));
const SetPasswordFrom = React.lazy(() => import("./pages/portal/account/partial/setUpPasswordFrom"));

function AnimatedRoute() {
  const location = useLocation();
  const { pathname } = useLocation();
  const [wasLoaded, setWasLoaded] = useState(false);
  const { Language } = useLanguageStore();

  useEffect(() => {
    document.body.dir = Language === "ar" ? "rtl" : "ltr";
    loadLocaleAsync(Language).then(() => setWasLoaded(true));
  }, [Language]);

  useEffect(() => {
    window.scrollTo({
      behavior: "instant",
      left: 0,
      top: 0,
    });
  }, [pathname]);

  if (!wasLoaded) return null;

  return (
    <AnimatePresence initial={false} key="mainBody" mode="wait">
      <TypesafeI18n locale={Language}>
        <Routes key={location.pathname} location={location}>
          <Route element={<Portal />} path="Portal/*" />
          <Route element={<Dashboard />} path="Dashboard/*" />
          <Route element={<AccountLayout />} path="supplier/confirm">
            <Route element={<SetPasswordFrom />} index />
          </Route>
          <Route element={<ViewError />} path="/error/*" />
          <Route element={<NotAllowed />} path="/notAllowed/*" />
          <Route element={<Navigate to="/Portal/" />} path="/" />
          <Route element={<Navigate to="/" />} path="*" />
        </Routes>
      </TypesafeI18n>
    </AnimatePresence>
  );
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retryDelay: 1000 * 5 * 1,
    },
  },
});
function App() {
  return (
    <Suspense fallback={<Loader />}>
      <BrowserRouter
        future={{
          v7_relativeSplatPath: true,
          v7_startTransition: true,
        }}>
        <AnimatedRoute />
      </BrowserRouter>
    </Suspense>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root") as Element);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <ToastContainer autoClose={5000} closeOnClick pauseOnHover position="top-center" rtl={false} />
    </QueryClientProvider>
  </React.StrictMode>,
);
